/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */
import _ from 'lodash';
import React from 'react';
import { decorate, computed, action, runInAction, observable } from 'mobx';
import { observer, inject } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { Button, Table } from 'semantic-ui-react';

import { displayError } from '@amzn/base-ui/dist/helpers/notification';

class ScEnvironmentAdsConnectionRow extends React.Component {
  constructor(props) {
    super(props);
    runInAction(() => {
      this.processingId = '';
      this.processingName = '';
    });
  }

  get environment() {
    return this.props.scEnvironment;
  }

  get envsStore() {
    return this.props.scEnvironmentsStore;
  }

  get connectionStore() {
    return this.envsStore.getScEnvConnectionStore(this.environment.id);
  }

  get connections() {
    return this.environment.getConnections(({ scheme }) => scheme === 'ads');
  }

  get dcvConnections() {
    if (!this.props.hasDcv) return [];
    return this.environment.getConnections(({ scheme }) => scheme === 'dcv');
  }

  handleConnect = async (id, { isDcvClient = false, path = '', port = '', connName = '' } = {}) => {
    const store = this.connectionStore;
    const connections = this.environment.connections;
    const connectInfo = _.find(connections, ['id', id]) || {};
    let url = connectInfo.url;

    runInAction(() => {
      this.processingId = id;
      this.processingName = connName;
    });

    try {
      if (!url) {
        const urlObj = await store.createConnectionUrl(id);
        url = urlObj.url;
      }

      if (url) {
        if (isDcvClient) {
          url = url.replace('https://', 'dcv://');
        }
        if (port) {
          url = `${url}:${port}`;
        }
        if (path) {
          url = `${url}/${path}`;
        }

        const loadUrlA = document.createElement('a');
        loadUrlA.href = url;
        loadUrlA.target = '_blank';
        loadUrlA.rel = 'noopener noreferrer';
        loadUrlA.click();
      }
    } catch (error) {
      displayError(error);
    } finally {
      runInAction(() => {
        this.processingId = '';
        this.processingName = '';
      });
    }
  };

  render() {
    const isDisabled = id => this.processingId !== id && !_.isEmpty(this.processingId);

    return (
      <Table.Row>
        <Table.Cell className="clearfix">
          {_.map(this.dcvConnections, item => (
            <>
              <Button
                floated="right"
                size="mini"
                primary
                disabled={isDisabled(item.id) || (this.processingId === item.id && this.processingName === 'dcv-browser')}
                loading={this.processingId === item.id && this.processingName === 'dcv-client'}
                onClick={() => this.handleConnect(item.id, { isDcvClient: true, connName: 'dcv-client' })}
                data-testid="connect-using-dcv-client"
              >
                DCV (Client)
              </Button>
              <Button
                floated="right"
                size="mini"
                primary
                disabled={isDisabled(item.id) || (this.processingId === item.id && this.processingName === 'dcv-client')}
                loading={this.processingId === item.id && this.processingName === 'dcv-browser'}
                onClick={() => this.handleConnect(item.id, { connName: 'dcv-browser' })}
                data-testid="connect-using-dcv-browser"
              >
                DCV (Browser)
              </Button>
            </>
          ))}
          {_.map(this.connections, item => (
            <>
              <Button
                floated="right"
                size="mini"
                primary
                disabled={isDisabled(item.id) || (this.processingId === item.id && this.processingName === 'dashboard')}
                loading={this.processingId === item.id && this.processingName === 'ide'}
                onClick={() => this.handleConnect(item.id, { path: 'ide/', connName: 'ide' })}
                data-testid="show-ads-vscode-button"
              >
                Open IDE
              </Button>
              <Button
                floated="right"
                size="mini"
                primary
                disabled={isDisabled(item.id) || (this.processingId === item.id && this.processingName === 'ide')}
                loading={this.processingId === item.id && this.processingName === 'dashboard'}
                onClick={() => this.handleConnect(item.id, { path: 'dashboard', connName: 'dashboard' })}
                data-testid="show-ads-connection-button"
              >
                Dashboard
              </Button>
            </>
          ))}
          <div className="mt1">Dataspace Controls</div>
        </Table.Cell>
      </Table.Row>
    );
  }
}

// see https://medium.com/@mweststrate/mobx-4-better-simpler-faster-smaller-c1fbc08008da
decorate(ScEnvironmentAdsConnectionRow, {
  processingId: observable,
  processingName: observable,
  envsStore: computed,
  environment: computed,
  connections: computed,
  handleConnect: action,
  connectionStore: computed,
});

export default inject('scEnvironmentsStore')(withRouter(observer(ScEnvironmentAdsConnectionRow)));
